import { useState } from 'react';
import { Card, Box, Autocomplete, TextField, useTheme } from '@mui/material';
import { AxisColorPicker } from './axis-color-picker';
import { useGetAllTagsQuery } from 'src/hooks/reactQueryApiHooks';
import { useAuthentication } from 'src/features/authentication/context';
import { AggregationOption, AggregationOptionLabel, AxisItemConfig } from 'src/types/trends/trends';
import { CloseIconV2 } from 'src/assets/icons/CloseIconV2';
import { NewSite } from 'src/types/sites/sites';

type Props = {
  inputId: string;
  siteId: string;
  tagId: string;
  aggregation: AggregationOption | null;
  onChangeItem: (id: string, patch: Partial<AxisItemConfig>) => void;
  onRemove: (id: string) => void;
  color: string;
  canRemove: boolean;
};

export const AxisDialogItem = ({
  inputId,
  siteId,
  tagId,
  aggregation,
  onChangeItem,
  onRemove,
  color,
  canRemove,
}: Props) => {
  const {
    customerId: { value: customerId },
    sites,
  } = useAuthentication();

  const theme = useTheme();
  const [selectedSite, setSelectedSite] = useState<NewSite | null>(
    () => sites?.value?.find((s: NewSite) => s.id === siteId) ?? null
  );

  const { tags } = useGetAllTagsQuery({
    customerId: customerId!,
    siteId: selectedSite?.id,
  });

  const selectedTagOption = tags?.find((t: { id: string }) => t.id === tagId) ?? null;

  const aggregationOptions = Object.values(AggregationOption);

  return (
    <Card sx={{ padding: '1rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
      <Box sx={{ display: 'flex', flexShrink: 0, gap: '5px' }}>
        <Box sx={{ width: '20px', height: '20px', borderRadius: '50%', background: color }} />
        <AxisColorPicker color={color} onChange={(color) => onChangeItem(inputId, { color })} />
      </Box>

      <Box sx={{ display: 'flex', flex: 1 }}>
        <Autocomplete
          sx={{ flex: 1 }}
          size="small"
          value={selectedSite}
          onChange={(_, site: NewSite | null) => {
            setSelectedSite(site);
            onChangeItem(inputId, { siteId: site?.id ?? '', siteName: site?.siteName ?? '' });
          }}
          renderInput={(params) => (
            <TextField {...params} label="Select Site" data-sm="select-site-input" />
          )}
          options={sites?.value ?? []}
          getOptionLabel={(option) => option.siteName}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              {option.siteName}
            </li>
          )}
          isOptionEqualToValue={(option, value) => option.id === value.id}
        />
      </Box>

      <Box sx={{ display: 'flex', flex: 1 }}>
        <Autocomplete
          sx={{ flex: 1 }}
          size="small"
          value={selectedTagOption}
          onChange={(_, selectedTag: any) => {
            if (selectedTag) {
              onChangeItem(inputId, {
                tagId: selectedTag.id ?? '',
                inputName: selectedTag.displayName ?? '',
                rawInputName: selectedTag.rawInputName ?? '',
                gatewayId: selectedTag.gatewayId ?? '',
                unit: selectedTag.rawUnit ?? '',
                dataType: selectedTag.dataType ?? '',
              });
            }
          }}
          renderInput={(params) => (
            <TextField {...params} label="Select Tag" data-sm="select-tag-input" />
          )}
          options={tags ?? []}
          getOptionLabel={(option) => option?.displayName}
          renderOption={(props, option) => (
            <li {...props} key={`${option.id}-${option.displayName}`}>
              {option.displayName}
            </li>
          )}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          disabled={!selectedSite}
        />
      </Box>

      <Box sx={{ display: 'flex', flex: '0 0 130px' }}>
        <Autocomplete<AggregationOption>
          sx={{ flex: 1 }}
          size="small"
          value={aggregation || null}
          onChange={(_, aggregation) => {
            if (aggregation) {
              onChangeItem(inputId, { aggregation });
            }
          }}
          renderInput={(params) => (
            <TextField {...params} label="Interval" data-sm="interval-input" />
          )}
          options={aggregationOptions}
          getOptionLabel={(option) => AggregationOptionLabel[option]}
          renderOption={(props, option) => (
            <li {...props} key={option}>
              {AggregationOptionLabel[option]}
            </li>
          )}
          isOptionEqualToValue={(option, value) => option === value}
        />
      </Box>

      {canRemove && (
        <Box
          sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
          onClick={() => onRemove(inputId)}
        >
          <CloseIconV2 fill={theme.palette.grey[600]} />
        </Box>
      )}
    </Card>
  );
};
